<template>
  <div class="order-info-card">
    <div class="order-info-title">{{ text.orderInfo }}</div>
    <div class="order-info-body">
      <div class="row company-row">
        <span class="label">{{ text.companyLabel }}：</span>
        <span class="company-name">{{ order.companyName }}</span>
      </div>
      <div class="row detail-row">
        <div class="detail-main">
          <p>{{ text.goodsLabel }}：{{ goodsName }}</p>
          <p v-if="durationText">
            {{ text.durationLabel }}：{{ durationText }}
          </p>
          <p v-if="validPeriod">
            {{ text.validPeriodLabel }}：{{ validPeriod }}
          </p>
        </div>
        <div class="detail-pay">
          <span class="label">{{ text.realPayLabel }}：</span>
          <span class="price">￥{{ order.price }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PRO_PLAN_TEXT } from "@/consts/proPlan";
import {
  planDurationText,
  formatDateDot,
  calcValidPeriod,
  toTimestamp,
} from "@/utils/proPlan";

/**
 * 订单信息卡（订单确认页 / 过期页 / 成功页 复用）
 * order: { companyName, goodsName, months, price, effectTime, expireTime }
 * currentExpireDate: 当前专业版到期日（可选），用于按需求规则推算生效/到期日期
 */
export default {
  name: "OrderInfoCard",
  props: {
    order: {
      type: Object,
      required: true,
    },
    // 是否展示有效期（确认页/成功页展示，过期页按设计也展示）
    showValidPeriod: {
      type: Boolean,
      default: true,
    },
    // 当前专业版到期日期（"yyyy-MM-dd"，免费版传 null）：
    // 付费版未过期时生效日期取「到期日 +1 天」，已过期/免费版取当天
    // （与 App 端一致，见 utils/proPlan.calcValidPeriod）
    currentExpireDate: {
      type: String,
      default: "",
    },
    // 企业版本 0免费 / 1专业 / 2旗舰（来自资格接口）。
    // 生效日规则要区分付费版与免费版：免费版即使有到期日也不做「+1 天」推算
    currentRankType: {
      type: [Number, String],
      default: null,
    },
    // 服务器当前时间（BaseResp.curDate，毫秒时间戳或字符串）：
    // 「生效日期 = 当天」一档以它为准，避免客户端时钟偏差导致与服务端算出的日期差一天
    serverNow: {
      type: [Number, String],
      default: "",
    },
  },
  data() {
    return { text: PRO_PLAN_TEXT };
  },
  computed: {
    goodsName() {
      const o = this.order || {};
      if (o.goodsName) return o.goodsName;
      return o.planName ? this.text.proPrefix + o.planName : "";
    },
    durationText() {
      return this.order.months
        ? planDurationText({ months: this.order.months })
        : "";
    },
    validPeriod() {
      const o = this.order || {};
      if (!this.showValidPeriod) return "";
      // 订单已生成生效/到期日期（支付成功、订单详情返回）时直接用
      if (o.effectTime && o.expireTime) {
        return `${formatDateDot(o.effectTime)} 至 ${formatDateDot(
          o.expireTime
        )}`;
      }
      // 否则按需求规则实时推算（下单时订单尚未生效，只有这样才有值可展示）。
      // 用服务器时间做「当天」，与后端计算生效日期的基准一致
      if (!o.months) return "";
      const serverTs = toTimestamp(this.serverNow);
      const { start, end } = calcValidPeriod({
        months: Number(o.months) || 1,
        now: isNaN(serverTs) ? new Date() : new Date(serverTs),
        currentExpireDate: this.currentExpireDate || null,
        rankType:
          this.currentRankType === null || this.currentRankType === ""
            ? null
            : Number(this.currentRankType),
      });
      return `${formatDateDot(start)} 至 ${formatDateDot(end)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.order-info-card {
  width: 442px;
  // 与右侧二维码区等高（.confirm-card 是 flex，默认 align-items:stretch），
  // 再让内部灰底卡片撑满剩余高度（禅道 #23483）：
  // 设计稿里左侧灰底卡片的底边**低于**右侧协议行，整体高度大于右侧；
  // 原先 .order-info-body 只有内容高度，底边停在协议行上方一大截，看起来左侧比右侧矮。
  display: flex;
  flex-direction: column;
}
.order-info-title {
  font-size: 20px;
  font-weight: 700;
  color: #3f454b;
  line-height: 22.5px;
}
.order-info-body {
  // 标题底 → 卡片顶：设计稿 1934:81 里标题框 y0 h23、卡片框 y41 → 间距 18
  margin-top: 18px;
  background: #f7f9fb;
  border-radius: 6px;
  // 撑满标题以下的剩余高度，使灰底卡片底边与右侧内容区底边对齐
  flex: 1;
}
.row {
  padding: 20px 16px;
}
.company-row {
  border-bottom: 1px solid #ebebeb;
  // 设计稿该行为 VERTICAL 自动布局、gap 10：
  // 原先 label 是行内元素 + .company-name 用 margin-top:10，实测间距只有 7（行内盒与块盒基线差异），
  // 改用 flex + gap 才能稳定拿到 10
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.label {
  font-size: 14px;
  // 设计稿 label 行盒高 20（14px 字 / 19.6 行高）；不设时全局行高会让盒子只有 14，
  // 导致下方企业名整体上移 6px
  line-height: 20px;
  color: #62717e;
}
.company-name {
  font-size: 20px;
  font-weight: 700;
  color: #3f454b;
  line-height: 22.5px;
}
// 明细行：设计稿 padding 上下各 10（原先 20，看上去「上下边距大了」）
.detail-row {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 10px 16px;
}
.detail-main {
  p {
    // 设计稿三行的行盒上下间距 40（行高 20 的框 + gap 20）。
    // 原先 16px 行高 + 12px 外边距 = 28，偏小；这里按 22（≈设计稿 22.4 行高）+ 18 补齐 40
    margin: 0 0 18px;
    line-height: 22px;
    font-size: 16px;
    color: #62717e;
    &:last-child {
      margin-bottom: 0;
    }
  }
  padding-bottom: 10px;
  border-bottom: 1px dashed #d7d7d7;
}
.detail-pay {
  display: flex;
  align-items: center;
  .label {
    font-size: 14px;
  }
  .price {
    font-size: 24px;
    font-weight: 700;
    color: #ff4f4f;
  }
}
</style>
